import { ReactNode, useMemo, useState } from 'react'
import { Box, BoxProps, ContainerProps, styled, Toolbar } from '@mui/material'

import { Feature, Flag, FlagType } from '@acre/utils'

import useToolbarInfo from '../../hooks/useToolbarInfo'
import { caseSidebarDrawWidth } from '../../theme/theme.mui'
import CaseSidebarDrawer from '../CaseSidebarDrawer'
import type { PageLayoutVariant } from './PageLayout.types'
import PageLayoutContainer from './PageLayoutContainer'

export interface PageLayoutProps extends Omit<BoxProps, 'maxWidth'>, Pick<ContainerProps, 'maxWidth'> {
  variant?: PageLayoutVariant
  containerProps?: ContainerProps
  sidebarChildren?: ReactNode
}

export const PageLayoutRoot = styled(Box, {
  name: 'PageLayout',
  slot: 'Root',
  overridesResolver: (_props, styles) => styles.root,
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'rightDrawerOpen',
})<PageLayoutProps & { rightDrawerOpen?: boolean }>(({ theme, variant = 'one-column', rightDrawerOpen }) => {
  let width = '100%'

  if (variant === 'two-column') {
    width = `calc(100% - ${theme.mixins?.sidebar?.width})`
  } else if (variant === 'two-column-with-sidebar') {
    width = `calc(100% - ${theme.mixins?.sidebar?.width} - 30px)`
  }

  let marginLeft = 'auto'

  if (variant === 'two-column' || variant === 'two-column-with-sidebar') {
    marginLeft = theme.mixins?.sidebar?.width?.toString() ?? '240px'
  }

  return {
    display: 'flex',
    flexDirection: 'column',
    marginLeft,
    flex: 1,

    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),

    width,
    [theme.breakpoints.up(1450)]: {
      ...(variant === 'two-column-with-sidebar' && {
        width: `calc(100% - ${theme.mixins?.sidebar?.width} - ${
          rightDrawerOpen ? `${caseSidebarDrawWidth}px` : '30px'
        })`,
      }),
    },
  }
})

const PageLayout = (props: PageLayoutProps) => {
  const { variant, maxWidth, children, sidebarChildren, containerProps, ...boxProps } = props
  const [open, setOpen] = useState(false)
  const toggleDrawerOpen = () => {
    setOpen(!open)
  }

  const { shouldRender } = useToolbarInfo()

  const containerMaxWidth = useMemo(() => {
    if (variant === 'two-column' || variant === 'two-column-with-sidebar') {
      return 'lg'
    } else if (maxWidth) {
      return maxWidth
    } else {
      return false
    }
  }, [maxWidth, variant])

  return (
    <>
      <PageLayoutRoot component="main" variant={variant} rightDrawerOpen={open} {...boxProps}>
        {shouldRender && <Toolbar />}
        <PageLayoutContainer
          variant={variant}
          rightDrawerOpen={open}
          maxWidth={containerMaxWidth}
          disableGutters={containerProps?.disableGutters}
        >
          <Box display="flex" flexDirection="column" width="100%">
            {children}
          </Box>
        </PageLayoutContainer>
      </PageLayoutRoot>
      {variant === 'two-column-with-sidebar' && (
        <Flag keyPath={[FlagType.Feature, Feature.CaseSidebarDrawer]} defaultValue={false}>
          <CaseSidebarDrawer open={open} toggleDrawerOpen={toggleDrawerOpen}>
            {sidebarChildren}
          </CaseSidebarDrawer>
        </Flag>
      )}
    </>
  )
}

export default PageLayout
